<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Archive, CheckCircle2, Save } from 'lucide-vue-next'
import { useToast } from '@/components/ui/toast'
import { useI18n } from 'vue-i18n'
import ProcessFlowDesigner from './ProcessFlowDesigner.vue'
import TurtleChartEditor from './TurtleChartEditor.vue'
import LinkedProcessSheet from './LinkedProcessSheet.vue'
import { normalizeTurtleData, serializeTurtleData, type TurtleProcessLink } from '@/utils/processTurtle'
import { initialProcessTab, visibleProcessTabs, type ProcessEditorTab } from '@/utils/processPresentation'

const props = defineProps<{
  process: any
  processes: any[]
  documents: any[]
  options: { users: any[]; functions: any[] }
  capabilities: any
}>()
const emit = defineEmits<{ (event: 'back'): void; (event: 'updated', process: any): void; (event: 'archived'): void; (event: 'open-process', id: number): void }>()
const { t } = useI18n()
const { toast } = useToast()
const activeTab = ref('details')
const saving = ref(false)
const diagramSaving = ref(false)
const releaseNote = ref('')
const form = ref<any>({})
const turtleData = ref<any>(normalizeTurtleData({}))
const linkedProcessSheetOpen = ref(false)
const selectedTurtleLinks = ref<TurtleProcessLink[]>([])
const readonly = computed(() => !props.capabilities?.can_edit)
const tabs = computed(() => visibleProcessTabs(form.value, !readonly.value))
let loadedProcessId: number | undefined

watch(() => props.process, (value) => {
  const processChanged = value?.id !== loadedProcessId
  loadedProcessId = value?.id
  form.value = JSON.parse(JSON.stringify(value || {}))
  form.value.related_process_ids = value?.related_process_ids || (value?.related_processes || []).map((item: any) => item.id)
  form.value.document_revision_ids = value?.document_revision_ids ||
    (value?.document_revisions?.length
      ? value.document_revisions.map((item: any) => item.id)
      : (value?.documents || []).map((item: any) => item.revision_id))
  turtleData.value = normalizeTurtleData(value?.turtle_data)
  const availableTabs = visibleProcessTabs(value, !readonly.value)
  if (processChanged || !availableTabs.includes(activeTab.value as ProcessEditorTab)) {
    activeTab.value = initialProcessTab(value, !readonly.value)
  }
}, { immediate: true, deep: true })

watch(readonly, () => {
  const availableTabs = visibleProcessTabs(form.value, !readonly.value)
  if (!availableTabs.includes(activeTab.value as ProcessEditorTab)) {
    activeTab.value = initialProcessTab(form.value, !readonly.value)
  }
})

function userName(user: any) {
  return [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email || `#${user.id}`
}

async function saveDetails() {
  saving.value = true
  try {
    const payload = {
      process: {
        code: form.value.code,
        title: form.value.title,
        category: form.value.category,
        objective: form.value.objective,
        scope: form.value.scope,
        owner_user_id: form.value.owner_user_id || null,
        responsible_function_id: form.value.responsible_function_id || null,
        position: form.value.position || 0,
        lock_version: form.value.lock_version,
        related_process_ids: form.value.related_process_ids || [],
        document_revision_ids: form.value.document_revision_ids || [],
      },
    }
    const response = await axios.patch(`/process_management/processes/${form.value.id}`, payload)
    emit('updated', response.data.process)
    toast({ title: t('processManagement.saved') })
  } catch (error: any) {
    toast({ title: t('processManagement.saveFailed'), description: error.response?.data?.error, variant: 'destructive' })
  } finally { saving.value = false }
}

async function saveTurtle() {
  saving.value = true
  try {
    const turtle_data = serializeTurtleData(turtleData.value)
    const response = await axios.patch(`/process_management/processes/${form.value.id}/turtle`, { turtle_data, lock_version: form.value.lock_version })
    emit('updated', response.data.process)
    toast({ title: t('processManagement.saved') })
  } catch (error: any) {
    toast({ title: t('processManagement.saveFailed'), description: error.response?.data?.error, variant: 'destructive' })
  } finally { saving.value = false }
}

async function saveDiagram(graph: any) {
  diagramSaving.value = true
  try {
    const response = await axios.patch(`/process_management/processes/${form.value.id}/diagram`, { flow_data: graph, lock_version: form.value.lock_version })
    emit('updated', response.data.process)
  } catch (error: any) {
    toast({ title: t('processManagement.saveFailed'), description: error.response?.data?.error, variant: 'destructive' })
    if (error.response?.status === 409) {
      const latest = await axios.get(`/process_management/processes/${form.value.id}`)
      emit('updated', latest.data.process)
    }
  } finally { diagramSaving.value = false }
}

async function releaseProcess() {
  try {
    const response = await axios.post(`/process_management/processes/${form.value.id}/release`, { note: releaseNote.value })
    releaseNote.value = ''
    emit('updated', response.data.process)
    toast({ title: t('processManagement.releaseComplete') })
  } catch (error: any) {
    toast({ title: t('processManagement.releaseFailed'), description: error.response?.data?.error, variant: 'destructive' })
  }
}

async function archiveProcess() {
  if (!window.confirm(t('processManagement.archiveConfirm'))) return
  await axios.post(`/process_management/processes/${form.value.id}/archive`, { lock_version: form.value.lock_version })
  emit('archived')
}

function openTurtleLinks(links: TurtleProcessLink[]) {
  selectedTurtleLinks.value = links
  linkedProcessSheetOpen.value = true
}

function openFullLinkedProcess(id: number) {
  linkedProcessSheetOpen.value = false
  emit('open-process', id)
}
</script>

<template>
  <div class="space-y-5">
    <div class="flex flex-wrap items-center gap-3">
      <Button type="button" variant="outline" size="sm" @click="emit('back')"><ArrowLeft class="mr-2 h-4 w-4" />{{ t('processManagement.back') }}</Button>
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ form.code }}</div>
        <h2 class="text-xl font-semibold text-slate-900">{{ form.title }}</h2>
      </div>
      <div class="ml-auto flex items-center gap-2 text-sm">
        <span v-if="form.released" class="flex items-center gap-1 text-emerald-700"><CheckCircle2 class="h-4 w-4" />v{{ form.release_number }}</span>
        <span v-if="form.unreleased_changes" class="rounded bg-amber-50 px-2 py-1 text-amber-700">{{ t('processManagement.unreleasedChanges') }}</span>
      </div>
    </div>

    <nav class="flex gap-1 overflow-x-auto border-b">
      <button v-for="tab in tabs" :key="tab" class="border-b-2 px-4 py-2 text-sm font-medium" :class="activeTab === tab ? 'border-[#80BA27] text-slate-900' : 'border-transparent text-slate-500'" @click="activeTab = tab">
        {{ t(`processManagement.tabs.${tab}`) }}
      </button>
    </nav>

    <section v-if="activeTab === 'details'" class="grid gap-5 rounded-xl border bg-white p-5 lg:grid-cols-2">
      <div><Label>{{ t('processManagement.code') }}</Label><Input v-model="form.code" :disabled="readonly" /></div>
      <div><Label>{{ t('processManagement.processTitle') }}</Label><Input v-model="form.title" :disabled="readonly" /></div>
      <div><Label>{{ t('processManagement.category') }}</Label><select v-model="form.category" :disabled="readonly" class="h-10 w-full rounded-md border bg-white px-3"><option v-for="category in ['management','core','support']" :key="category" :value="category">{{ t(`processManagement.categories.${category}`) }}</option></select></div>
      <div><Label>{{ t('processManagement.owner') }}</Label><select v-model="form.owner_user_id" :disabled="readonly" class="h-10 w-full rounded-md border bg-white px-3"><option :value="null">—</option><option v-for="user in options.users" :key="user.id" :value="user.id">{{ userName(user) }}</option></select></div>
      <div class="lg:col-span-2"><Label>{{ t('processManagement.objective') }}</Label><textarea v-model="form.objective" :disabled="readonly" rows="4" class="w-full rounded-md border p-3" /></div>
      <div class="lg:col-span-2"><Label>{{ t('processManagement.scope') }}</Label><textarea v-model="form.scope" :disabled="readonly" rows="4" class="w-full rounded-md border p-3" /></div>
      <div><Label>{{ t('processManagement.function') }}</Label><select v-model="form.responsible_function_id" :disabled="readonly" class="h-10 w-full rounded-md border bg-white px-3"><option :value="null">—</option><option v-for="item in options.functions" :key="item.id" :value="item.id">{{ item.displayname || item.functionname }}</option></select></div>
      <div><Label>{{ t('processManagement.position') }}</Label><Input v-model.number="form.position" type="number" :disabled="readonly" /></div>
      <div class="lg:col-span-2"><Label>{{ t('processManagement.relatedProcesses') }}</Label><div class="mt-2 grid gap-2 sm:grid-cols-2"><label v-for="item in processes.filter((item) => item.id !== form.id)" :key="item.id" class="flex items-center gap-2 rounded border p-2 text-sm"><input v-model="form.related_process_ids" type="checkbox" :value="item.id" :disabled="readonly" />{{ item.code }} · {{ item.title }}</label></div></div>
      <div class="lg:col-span-2"><Label>{{ t('processManagement.documents') }}</Label><div class="mt-2 grid gap-2 sm:grid-cols-2"><label v-for="item in documents.filter((item) => item.current_revision)" :key="item.id" class="flex items-center gap-2 rounded border p-2 text-sm"><input v-model="form.document_revision_ids" type="checkbox" :value="item.current_revision.id" :disabled="readonly" />{{ item.title }} · v{{ item.current_revision.revision_number }}</label></div></div>
      <div v-if="!readonly" class="flex gap-2 lg:col-span-2"><Button @click="saveDetails" :disabled="saving"><Save class="mr-2 h-4 w-4" />{{ t('processManagement.save') }}</Button><Button variant="outline" class="text-red-600" @click="archiveProcess"><Archive class="mr-2 h-4 w-4" />{{ t('processManagement.archive') }}</Button></div>
    </section>

    <section v-if="activeTab === 'turtle'" class="rounded-xl border bg-white p-5">
      <TurtleChartEditor v-model="turtleData" :process="form" :processes="processes.filter((item) => item.id !== form.id)" :readonly="readonly" @open-links="openTurtleLinks" />
      <Button v-if="!readonly" class="mt-4" @click="saveTurtle" :disabled="saving"><Save class="mr-2 h-4 w-4" />{{ t('processManagement.save') }}</Button>
    </section>

    <section v-if="activeTab === 'flow'" class="rounded-xl border bg-white p-4">
      <ProcessFlowDesigner :model-value="form.flow_data || { nodes: [], edges: [] }" :linked-processes="processes.filter((item) => item.id !== form.id)" :readonly="readonly" :saving="diagramSaving" @save="saveDiagram" />
    </section>

    <section v-if="activeTab === 'releases'" class="space-y-4">
      <div v-if="capabilities.can_release" class="rounded-xl border bg-white p-5"><Label>{{ t('processManagement.releaseNote') }}</Label><textarea v-model="releaseNote" rows="3" class="mt-1 w-full rounded-md border p-3" /><Button class="mt-3 bg-[#80BA27] text-white hover:bg-[#70a522]" @click="releaseProcess">{{ t('processManagement.release') }}</Button></div>
      <div class="overflow-hidden rounded-xl border bg-white"><div v-for="release in (form.releases || [])" :key="release.id" class="flex items-start justify-between border-b p-4 last:border-0"><div><div class="font-medium">Version {{ release.number }}</div><div class="text-sm text-slate-500">{{ release.note || '—' }}</div></div><time class="text-xs text-slate-500">{{ new Date(release.created_at).toLocaleString() }}</time></div><p v-if="!form.releases?.length" class="p-5 text-sm text-slate-500">{{ t('processManagement.noReleases') }}</p></div>
    </section>

    <LinkedProcessSheet v-model:open="linkedProcessSheetOpen" :links="selectedTurtleLinks" :processes="processes" @open-process="openFullLinkedProcess" />
  </div>
</template>
